import { apiClient } from './api-client';
import { filesApi } from './files';
import type {
  AblyTokenRequest,
  AblyTokenResponse,
  ActiveCallResponse,
  CallDebugEventRequest,
  CallDebugEventResponse,
  CallRecordingPlaybackUrlResponse,
  CallRecordingControlRequest,
  CallRecordingControlResponse,
  CallRecordingsResponse,
  CallQualitySummaryResponse,
  AddConferenceParticipantRequest,
  AddConferenceParticipantResponse,
  CallCenterCallsResponse,
  CallCenterContactsResponse,
  CallCenterHandlingSettingsResponse,
  CallCenterStaffAvailabilityResponse,
  CallCenterVoicemailPlaybackUrlResponse,
  CallCenterVoicemailReadResponse,
  CallCenterVoicemailsResponse,
  CallCenterDeviceHeartbeatRequest,
  CallCenterDeviceHeartbeatResponse,
  CallSupervisorControlRequest,
  CallSupervisorControlResponse,
  CallTranscriptionControlRequest,
  CallTranscriptionControlResponse,
  ClaimIncomingCallRequest,
  ClaimIncomingCallResponse,
  CreateDirectRoomRequest,
  EndCallResponse,
  HoldConferenceParticipantsRequest,
  HoldConferenceParticipantsResponse,
  HoldCallResponse,
  MessagingPolicyEvent,
  MessagingOperationalOverview,
  MessagingRoutingTriageListResponse,
  MessagingCallResponse,
  MessagingRoomDetails,
  MessagingRoomListResponse,
  MessagingRoomPageResponse,
  MessagingRoomResponse,
  MessagingTextThreadListResponse,
  MessagingTextThreadResponse,
  MessagingTextContactSearchResponse,
  CreateMessagingTextThreadRequest,
  CreateMessagingTextThreadResponse,
  DeleteMessagingTextThreadsRequest,
  DeleteMessagingTextThreadsResponse,
  SendMessagingTextMessageRequest,
  SendMessagingTextMessageResponse,
  OpenQuoteRoomRequest,
  OpenQuoteRoomResponse,
  MarkMessagingRoomReadRequest,
  MarkMessagingRoomReadResponse,
  MarkMessagingRoomPresenceRequest,
  MarkMessagingRoomPresenceResponse,
  MobileCallEventRequest,
  MobileCallEventResponse,
  MobileDeviceDeactivateResponse,
  MobileDeviceRegistrationRequest,
  MobileDeviceRegistrationResponse,
  PrepareWebRtcCallRequest,
  PrepareWebRtcCallResponse,
  RecordWebRtcCallRequest,
  RecordWebRtcCallResponse,
  ResumeCallResponse,
  RecordMessagingMessageRequest,
  RecordMessagingMessageResponse,
  SendMessagingRoomMessageRequest,
  SendMessagingRoomMessageResponse,
  ClarifyMessagingRoutingTriageRequest,
  ClarifyMessagingRoutingTriageResponse,
  CreateClickToDialIntentRequest,
  CreateClickToDialIntentResponse,
  ResolveMessagingRoutingTriageRequest,
  ResolveMessagingRoutingTriageResponse,
  ScheduleMessagingMeetRequest,
  ProvisionSipEndpointRequest,
  ProvisionSipEndpointResponse,
  SipEndpointListResponse,
  SipEndpointResponse,
  UpdateSipEndpointRequest,
  StartCustomCallRequest,
  StartCustomCallResponse,
  SyncWebRtcCallProviderIdsRequest,
  SyncWebRtcCallProviderIdsResponse,
  TransferCallRequest,
  TransferCallResponse,
  UpdateCallCenterHandlingSettingsRequest,
  UpdateCallCenterStaffAvailabilityRequest,
  ProvisionStaffWebRtcCredentialRequest,
  ProvisionStaffZoiperCredentialRequest,
  ProvisionStaffZoiperCredentialResponse,
  StaffZoiperCredentialResponse,
  StaffWebRtcCredentialResponse,
  StartMessagingCallRequest,
  UpdateStaffWebRtcCredentialRequest,
  WebRtcTokenResponse,
  CallingSetupStatusResponse,
  CallFallbackNumber,
  StartFallbackNumberVerificationRequest,
  ConfirmFallbackNumberVerificationRequest,
  UpdateFallbackNumberRequest,
} from '@jema-manager/shared/types/messaging.types';

type AttachmentPresignResponse = {
  uploadUrl: string;
  bucketName: string;
  storagePath: string;
  expiresIn: number;
  attachmentReference?: {
    id?: string;
    roomId?: string;
    storageFileId?: string;
    filename?: string;
    contentType?: string;
    sizeBytes?: number;
    clinicalSensitive?: boolean;
  };
};

export const messagingApi = {
  listRooms: () => apiClient.get<MessagingRoomListResponse>('/messaging/rooms'),

  listRoomsPage: (input: { limit?: number; cursor?: string } = {}) =>
    apiClient.get<MessagingRoomPageResponse>('/messaging/rooms/page', input),

  listTextThreads: () => apiClient.get<MessagingTextThreadListResponse>('/messaging/texts/threads'),

  searchTextContacts: (query: string) =>
    apiClient.get<MessagingTextContactSearchResponse>('/messaging/texts/contacts', { query }),

  createTextThread: (input: CreateMessagingTextThreadRequest) =>
    apiClient.post<CreateMessagingTextThreadResponse>('/messaging/texts/threads', input),

  deleteTextThreads: (input: DeleteMessagingTextThreadsRequest) =>
    apiClient.delete<DeleteMessagingTextThreadsResponse>('/messaging/texts/threads', {
      body: JSON.stringify(input),
    }),

  getTextThread: (threadId: string) =>
    apiClient.get<MessagingTextThreadResponse>(
      `/messaging/texts/threads/${encodeURIComponent(threadId)}`
    ),

  sendTextMessage: (threadId: string, input: SendMessagingTextMessageRequest) =>
    apiClient.post<SendMessagingTextMessageResponse>(
      `/messaging/texts/threads/${encodeURIComponent(threadId)}/messages`,
      input
    ),

  openQuoteRoom: (input: OpenQuoteRoomRequest) =>
    apiClient.post<OpenQuoteRoomResponse>('/messaging/rooms/quote', input),

  createDirectRoom: (input: CreateDirectRoomRequest) =>
    apiClient.post<{ room: MessagingRoomDetails }>('/messaging/rooms/direct', input),

  recordMessage: (roomId: string, input: RecordMessagingMessageRequest) =>
    apiClient.post<RecordMessagingMessageResponse>(
      `/messaging/rooms/${encodeURIComponent(roomId)}/messages/record`,
      input
    ),

  sendRoomMessage: (roomId: string, input: SendMessagingRoomMessageRequest) =>
    apiClient.post<SendMessagingRoomMessageResponse>(
      `/messaging/rooms/${encodeURIComponent(roomId)}/messages`,
      input
    ),

  markRoomRead: (roomId: string, input: MarkMessagingRoomReadRequest) =>
    apiClient.post<MarkMessagingRoomReadResponse>(
      `/messaging/rooms/${encodeURIComponent(roomId)}/read`,
      input
    ),

  markRoomPresence: (roomId: string, input: MarkMessagingRoomPresenceRequest) =>
    apiClient.post<MarkMessagingRoomPresenceResponse>(
      `/messaging/rooms/${encodeURIComponent(roomId)}/presence`,
      input
    ),

  getRoom: (roomId: string) =>
    apiClient.get<MessagingRoomResponse>(`/messaging/rooms/${encodeURIComponent(roomId)}`),

  createAblyToken: (input: AblyTokenRequest = {}) =>
    apiClient.post<AblyTokenResponse>('/messaging/ably/token', input),

  createAttachmentPresign: (
    roomId: string,
    input: { filename: string; mimeType: string; sizeBytes: number }
  ) =>
    apiClient.post<AttachmentPresignResponse>(
      `/messaging/rooms/${encodeURIComponent(roomId)}/attachments/presign`,
      input
    ),

  uploadAttachment: async (roomId: string, file: File) => {
    const presign = await messagingApi.createAttachmentPresign(roomId, {
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    });

    await apiClient.uploadFile(presign.uploadUrl, file, file.type || 'application/octet-stream');

    const record = await filesApi.recordFile({
      bucket_name: presign.bucketName,
      storage_path: presign.storagePath,
      original_filename: file.name,
      mime_type: file.type || 'application/octet-stream',
      file_size: file.size,
      category: 'messaging-attachment',
      is_public: false,
      entity_type: 'messaging_room',
      entity_id: roomId,
    });
    return {
      fileId: record.id,
      filename: record.original_filename,
      contentType: record.mime_type,
      sizeBytes: record.file_size,
      storagePath: record.storage_path,
    };
  },

  createInstantMeet: (
    roomId: string,
    input: { connectionId: string; calendarId?: string; title?: string; specialty?: string }
  ) => apiClient.post(`/messaging/rooms/${encodeURIComponent(roomId)}/meet/instant`, input),

  scheduleMeet: (roomId: string, input: ScheduleMessagingMeetRequest) =>
    apiClient.post(`/messaging/rooms/${encodeURIComponent(roomId)}/meet/schedule`, input),

  startCall: (roomId: string, input: StartMessagingCallRequest) =>
    apiClient.post<MessagingCallResponse>(
      `/messaging/rooms/${encodeURIComponent(roomId)}/calls`,
      input
    ),

  endCall: (roomId: string, callId: string) =>
    apiClient.post<MessagingCallResponse>(
      `/messaging/rooms/${encodeURIComponent(roomId)}/calls/${encodeURIComponent(callId)}/end`,
      {}
    ),

  listCallCenterCalls: (params?: {
    patientId?: string | null;
    direction?: 'inbound' | 'outbound';
    status?: string;
    limit?: number;
    cursor?: string;
    startDate?: string;
    endDate?: string;
    sort?: 'newest' | 'oldest';
  }) =>
    apiClient.get<CallCenterCallsResponse>('/messaging/calls', {
      ...params,
      patientId: params?.patientId ?? undefined,
    }),

  listCallCenterVoicemails: (params?: {
    patientId?: string | null;
    status?: string;
    limit?: number;
    cursor?: string;
    startDate?: string;
    endDate?: string;
    sort?: 'newest' | 'oldest';
  }) =>
    apiClient.get<CallCenterVoicemailsResponse>('/messaging/calls/voicemails', {
      ...params,
      patientId: params?.patientId ?? undefined,
    }),

  markCallCenterVoicemailRead: (voicemailId: string) =>
    apiClient.post<CallCenterVoicemailReadResponse>(
      `/messaging/calls/voicemails/${encodeURIComponent(voicemailId)}/read`,
      {}
    ),

  markCallCenterVoicemailUnread: (voicemailId: string) =>
    apiClient.post<CallCenterVoicemailReadResponse>(
      `/messaging/calls/voicemails/${encodeURIComponent(voicemailId)}/unread`,
      {}
    ),

  getCallCenterVoicemailPlaybackUrl: (voicemailId: string) =>
    apiClient.get<CallCenterVoicemailPlaybackUrlResponse>(
      `/messaging/calls/voicemails/${encodeURIComponent(voicemailId)}/playback-url`
    ),

  listCallRecordings: (callId: string) =>
    apiClient.get<CallRecordingsResponse>(
      `/messaging/calls/${encodeURIComponent(callId)}/recordings`
    ),

  getCallRecordingPlaybackUrl: (recordingId: string) =>
    apiClient.get<CallRecordingPlaybackUrlResponse>(
      `/messaging/calls/recordings/${encodeURIComponent(recordingId)}/playback-url`
    ),

  controlCallRecording: (callId: string, input: CallRecordingControlRequest) =>
    apiClient.post<CallRecordingControlResponse>(
      `/messaging/calls/${encodeURIComponent(callId)}/recording-control`,
      input
    ),

  controlCallTranscription: (callId: string, input: CallTranscriptionControlRequest) =>
    apiClient.post<CallTranscriptionControlResponse>(
      `/messaging/calls/${encodeURIComponent(callId)}/transcription-control`,
      input
    ),

  controlCallSupervisor: (callId: string, input: CallSupervisorControlRequest) =>
    apiClient.post<CallSupervisorControlResponse>(
      `/messaging/calls/${encodeURIComponent(callId)}/supervisor-control`,
      input
    ),

  recordCallDebugEvent: (callId: string, input: CallDebugEventRequest) =>
    apiClient.post<CallDebugEventResponse>(
      `/messaging/calls/${encodeURIComponent(callId)}/debug-events`,
      input
    ),

  getCallQualitySummary: (callId: string) =>
    apiClient.get<CallQualitySummaryResponse>(
      `/messaging/calls/${encodeURIComponent(callId)}/quality-summary`
    ),

  listActiveCallCenterCalls: () => apiClient.get<ActiveCallResponse>('/messaging/calls/active'),

  getCallCenterStaffAvailability: () =>
    apiClient.get<CallCenterStaffAvailabilityResponse>('/messaging/calls/availability'),

  updateCallCenterStaffAvailability: (input: UpdateCallCenterStaffAvailabilityRequest) =>
    apiClient.put<CallCenterStaffAvailabilityResponse>('/messaging/calls/availability', input),

  heartbeatCallCenterDevice: (input: CallCenterDeviceHeartbeatRequest) =>
    apiClient.post<CallCenterDeviceHeartbeatResponse>('/messaging/calls/devices/heartbeat', input),

  createWebRtcToken: (deviceId: string) =>
    apiClient.get<WebRtcTokenResponse>('/messaging/calls/webrtc-token', { deviceId }),

  getWebRtcCredential: () =>
    apiClient.get<StaffWebRtcCredentialResponse>('/messaging/calls/webrtc-credential'),

  updateWebRtcCredential: (input: UpdateStaffWebRtcCredentialRequest) =>
    apiClient.put<StaffWebRtcCredentialResponse>('/messaging/calls/webrtc-credential', input),

  deleteWebRtcCredential: () =>
    apiClient.delete<StaffWebRtcCredentialResponse>('/messaging/calls/webrtc-credential'),

  getManagedWebRtcCredential: (staffUserId: string) =>
    apiClient.get<StaffWebRtcCredentialResponse>(
      `/messaging/calls/webrtc-credentials/${encodeURIComponent(staffUserId)}`
    ),

  updateManagedWebRtcCredential: (staffUserId: string, input: UpdateStaffWebRtcCredentialRequest) =>
    apiClient.put<StaffWebRtcCredentialResponse>(
      `/messaging/calls/webrtc-credentials/${encodeURIComponent(staffUserId)}`,
      input
    ),

  provisionManagedWebRtcCredential: (
    staffUserId: string,
    input: ProvisionStaffWebRtcCredentialRequest
  ) =>
    apiClient.post<StaffWebRtcCredentialResponse>(
      `/messaging/calls/webrtc-credentials/${encodeURIComponent(staffUserId)}/provision`,
      input
    ),

  deleteManagedWebRtcCredential: (staffUserId: string) =>
    apiClient.delete<StaffWebRtcCredentialResponse>(
      `/messaging/calls/webrtc-credentials/${encodeURIComponent(staffUserId)}`
    ),

  getManagedZoiperCredential: (staffUserId: string) =>
    apiClient.get<StaffZoiperCredentialResponse>(
      `/messaging/calls/zoiper-credentials/${encodeURIComponent(staffUserId)}`
    ),

  provisionManagedZoiperCredential: (
    staffUserId: string,
    input: ProvisionStaffZoiperCredentialRequest
  ) =>
    apiClient.post<ProvisionStaffZoiperCredentialResponse>(
      `/messaging/calls/zoiper-credentials/${encodeURIComponent(staffUserId)}/provision`,
      input
    ),

  revokeManagedZoiperCredential: (staffUserId: string) =>
    apiClient.delete<StaffZoiperCredentialResponse>(
      `/messaging/calls/zoiper-credentials/${encodeURIComponent(staffUserId)}`
    ),

  listManagedSipEndpoints: (staffUserId: string) =>
    apiClient.get<SipEndpointListResponse>(
      `/messaging/calls/sip-endpoints/${encodeURIComponent(staffUserId)}`
    ),

  provisionManagedSipEndpoint: (staffUserId: string, input: ProvisionSipEndpointRequest) =>
    apiClient.post<ProvisionSipEndpointResponse>(
      `/messaging/calls/sip-endpoints/${encodeURIComponent(staffUserId)}`,
      input
    ),

  updateManagedSipEndpoint: (
    staffUserId: string,
    endpointId: string,
    input: UpdateSipEndpointRequest
  ) =>
    apiClient.patch<SipEndpointResponse>(
      `/messaging/calls/sip-endpoints/${encodeURIComponent(staffUserId)}/${encodeURIComponent(endpointId)}`,
      input
    ),

  refreshManagedSipEndpointRegistration: (staffUserId: string, endpointId: string) =>
    apiClient.post<SipEndpointResponse>(
      `/messaging/calls/sip-endpoints/${encodeURIComponent(staffUserId)}/${encodeURIComponent(endpointId)}/refresh-registration`,
      {}
    ),

  revokeManagedSipEndpoint: (staffUserId: string, endpointId: string) =>
    apiClient.delete<SipEndpointResponse>(
      `/messaging/calls/sip-endpoints/${encodeURIComponent(staffUserId)}/${encodeURIComponent(endpointId)}`
    ),

  getCallingSetupStatus: () =>
    apiClient.get<CallingSetupStatusResponse>('/messaging/calls/setup-status'),

  startFallbackNumberVerification: (input: StartFallbackNumberVerificationRequest) =>
    apiClient.post<{ fallbackNumber: CallFallbackNumber }>(
      '/messaging/calls/fallback-number/verifications',
      input
    ),

  confirmFallbackNumberVerification: (input: ConfirmFallbackNumberVerificationRequest) =>
    apiClient.post<{ fallbackNumber: CallFallbackNumber }>(
      '/messaging/calls/fallback-number/verifications/confirm',
      input
    ),

  resendFallbackNumberVerification: () =>
    apiClient.post<{ fallbackNumber: CallFallbackNumber }>(
      '/messaging/calls/fallback-number/verifications/resend',
      {}
    ),

  updateFallbackNumber: (input: UpdateFallbackNumberRequest) =>
    apiClient.patch<{ fallbackNumber: CallFallbackNumber }>(
      '/messaging/calls/fallback-number',
      input
    ),

  removeFallbackNumber: () =>
    apiClient.delete<{ removed: boolean }>('/messaging/calls/fallback-number'),

  createClickToDialIntent: (input: CreateClickToDialIntentRequest) =>
    apiClient.post<CreateClickToDialIntentResponse>('/messaging/calls/click-to-dial', input),

  startClickToDialBrowserFallback: (intentId: string) =>
    apiClient.post<CreateClickToDialIntentResponse>(
      `/messaging/calls/click-to-dial/${encodeURIComponent(intentId)}/browser-fallback`,
      {}
    ),

  cancelClickToDialIntent: (intentId: string) =>
    apiClient.post<CreateClickToDialIntentResponse>(
      `/messaging/calls/click-to-dial/${encodeURIComponent(intentId)}/cancel`,
      {}
    ),

  startCustomCall: (input: StartCustomCallRequest) =>
    apiClient.post<StartCustomCallResponse>('/messaging/calls', input),

  prepareWebRtcCall: (input: PrepareWebRtcCallRequest) =>
    apiClient.post<PrepareWebRtcCallResponse>('/messaging/calls/webrtc/prepare', input),

  recordWebRtcCall: (input: RecordWebRtcCallRequest) =>
    apiClient.post<RecordWebRtcCallResponse>('/messaging/calls/webrtc', input),

  syncWebRtcCallProviderIds: ({
    callLogId,
    input,
  }: {
    callLogId: string;
    input: SyncWebRtcCallProviderIdsRequest;
  }) =>
    apiClient.patch<SyncWebRtcCallProviderIdsResponse>(
      `/messaging/calls/${encodeURIComponent(callLogId)}/webrtc-provider-ids`,
      input
    ),

  claimIncomingCall: (callLogId: string, input: ClaimIncomingCallRequest = {}) =>
    apiClient.post<ClaimIncomingCallResponse>(
      `/messaging/calls/${encodeURIComponent(callLogId)}/claim`,
      input
    ),

  recordMobileCallEvent: (callLogId: string, input: MobileCallEventRequest) =>
    apiClient.post<MobileCallEventResponse>(
      `/messaging/calls/${encodeURIComponent(callLogId)}/mobile-events`,
      input
    ),

  registerMobileDevice: (input: MobileDeviceRegistrationRequest) =>
    apiClient.post<MobileDeviceRegistrationResponse>('/mobile/devices', input),

  deactivateMobileDevice: (deviceId: string) =>
    apiClient.delete<MobileDeviceDeactivateResponse>(
      `/mobile/devices/${encodeURIComponent(deviceId)}`
    ),

  searchCallCenterContacts: (params: {
    query?: string;
    limit?: number;
    type?: 'patient' | 'doctor' | 'staff' | 'all';
  }) => apiClient.get<CallCenterContactsResponse>('/messaging/calls/contacts', params),

  holdCallCenterCall: (callLogId: string) =>
    apiClient.post<HoldCallResponse>(`/messaging/calls/${encodeURIComponent(callLogId)}/hold`, {}),

  resumeCallCenterCall: (callLogId: string) =>
    apiClient.post<ResumeCallResponse>(
      `/messaging/calls/${encodeURIComponent(callLogId)}/resume`,
      {}
    ),

  transferCallCenterCall: (callLogId: string, input: TransferCallRequest) =>
    apiClient.post<TransferCallResponse>(
      `/messaging/calls/${encodeURIComponent(callLogId)}/transfer`,
      input
    ),

  addCallCenterConferenceParticipant: (callLogId: string, input: AddConferenceParticipantRequest) =>
    apiClient.post<AddConferenceParticipantResponse>(
      `/messaging/calls/${encodeURIComponent(callLogId)}/conference/participants`,
      input
    ),

  holdCallCenterConferenceParticipants: (
    callLogId: string,
    input: HoldConferenceParticipantsRequest
  ) =>
    apiClient.post<HoldConferenceParticipantsResponse>(
      `/messaging/calls/${encodeURIComponent(callLogId)}/conference/hold`,
      input
    ),

  endCallCenterCall: (callLogId: string) =>
    apiClient.post<EndCallResponse>(`/messaging/calls/${encodeURIComponent(callLogId)}/end`, {}),

  getCallCenterHandlingSettings: () =>
    apiClient.get<CallCenterHandlingSettingsResponse>('/messaging/calls/handling-settings'),

  updateCallCenterHandlingSettings: (input: UpdateCallCenterHandlingSettingsRequest) =>
    apiClient.put<CallCenterHandlingSettingsResponse>('/messaging/calls/handling-settings', input),

  archiveRoom: (
    roomId: string,
    input: { storageFileId?: string | null; storagePath: string; checksum?: string | null }
  ) => apiClient.post(`/messaging/rooms/${encodeURIComponent(roomId)}/archive`, input),

  listPolicyEvents: () =>
    apiClient.get<{ events: MessagingPolicyEvent[] }>('/messaging/policy-events'),

  getOperationalOverview: () =>
    apiClient.get<MessagingOperationalOverview>('/messaging/operations/overview'),

  listRoutingTriage: () =>
    apiClient.get<MessagingRoutingTriageListResponse>('/messaging/routing-triage'),

  resolveRoutingTriage: (input: ResolveMessagingRoutingTriageRequest) =>
    apiClient.post<ResolveMessagingRoutingTriageResponse>(
      '/messaging/routing-triage/bulk-resolve',
      input
    ),

  clarifyRoutingTriage: (decisionId: string, input: ClarifyMessagingRoutingTriageRequest) =>
    apiClient.post<ClarifyMessagingRoutingTriageResponse>(
      `/messaging/routing-triage/${encodeURIComponent(decisionId)}/clarify`,
      input
    ),

  reviewPolicyEvent: (eventId: string, decision: 'reviewed' | 'dismissed' | 'escalated') =>
    apiClient.post<{ event: MessagingPolicyEvent }>(
      `/messaging/policy-events/${encodeURIComponent(eventId)}/review`,
      { decision }
    ),
};
